import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Desktop, ArrowLeft } from "phosphor-react";

/**
 * DesktopRequiredMessage - A consistent message component for features that require desktop
 * 
 * Props:
 * @param {string} featureName - Name of the feature (for accessibility)
 * @param {string} title - Title text (default: "Desktop Required")
 * @param {string} message - Body message text
 * @param {string} fallbackPath - Path to navigate to on "Go Back" (default: "/dashboard")
 * @param {boolean} showGoBack - Whether to show the Go Back button (default: true)
 */
export default function DesktopRequiredMessage({
  featureName = "This feature",
  title = "Desktop Required",
  message = "This feature is available on desktop only. Please use a computer to access it.",
  fallbackPath = "/dashboard",
  showGoBack = true,
}) {
  const navigate = useNavigate();

  const handleGoBack = () => {
    // Try to go back in history, fallback to specified path
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallbackPath);
    }
  };

  return (
    <div 
      className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
      role="alert"
      aria-label={`${featureName} requires a desktop device`}
      data-testid="desktop-required-message"
    >
      {/* Icon */}
      <div className="w-20 h-20 rounded-full bg-zinc-100 flex items-center justify-center mb-6">
        <Desktop size={40} className="text-zinc-400" weight="duotone" />
      </div>

      {/* Title */}
      <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-zinc-900 mb-3">
        {title}
      </h1>

      {/* Message */}
      <p className="text-zinc-600 max-w-md mb-8 leading-relaxed">
        {message}
      </p>

      {/* Go Back Button */}
      {showGoBack && (
        <Button
          onClick={handleGoBack}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase text-sm"
          data-testid="desktop-required-go-back-btn"
        >
          <ArrowLeft size={18} className="mr-2" />
          Go Back
        </Button>
      )}
    </div>
  );
}
